/**
 * Value-based schema refinement.
 *
 * Every static `propagateSchema` is a conservative best-effort
 * prediction: aggregate/time-bucket/reshape outputs, no-propagator
 * verbs (`join`, runtime-width `spread`/`pivot`), and any `?? unknownCol`
 * fallback all emit `unknown`, and `unknown` is monotonically
 * contagious — once a column is `unknown` every downstream verb keeps
 * it `unknown`, because the engine never re-derives a type from data.
 * That single gap is the root of the pervasive "column is `unknown`,
 * so `(col … sub)` / `join` keys / `unnest` fail on perfectly good
 * data" problem.
 *
 * This module closes it: at the materialisation boundary
 * (`runtime/arquero.fromTable`) we re-derive a concrete `ColumnType`
 * for any `unknown` column from its actual cell values. Strictly
 * additive — see {@link refineSchema}.
 *
 * A `Dataset` cell IS synthesized as `nested-dataset`, with the inner
 * schema read off the cell (carried whether the cell is lazy or
 * materialised). A column whose cells are datasets IS a TS-ref /
 * nested column; erasing that to `unknown` is what silently broke
 * every post-join per-cell `unnest` / `time-bucket` on good data (the
 * per-cell lift dispatch only engages on a `nested-dataset` schema
 * entry). Arrays still stay `unknown` (element type is not cheaply
 * unifiable here).
 */

import { type Cell, type Columns, Dataset, isUnitCell, unifiedUnitOfCells } from '../value.ts';
import {
  booleanCol,
  type ColumnType,
  columnTypesEqual,
  isNumericColumn,
  nestedDatasetCol,
  nullableCol,
  numberCol,
  replaceUnit,
  type Schema,
  schemaFromEntries,
  stringCol,
  structCol,
  timestampCol,
  unifiedUnitOfTypes,
  unknownCol,
  unwrapNullable,
  withUnit,
} from './types.ts';

/**
 * Best-effort `ColumnType` for a single non-null cell value. Conservative:
 * anything we can't classify cheaply and unambiguously (arrays, `Dataset`
 * cells, exotic objects) returns `unknownCol` rather than guess. A
 * unit-boxed number contributes a unit-stamped `number` — the ONE place a
 * column-level unit is derived (from cells, never computed by ops).
 */
const inferOne = (v: unknown): ColumnType => {
  if (typeof v === 'number') {
    return numberCol;
  }
  if (isUnitCell(v)) {
    return withUnit(numberCol, v.unit);
  }
  if (typeof v === 'string') {
    return stringCol;
  }
  if (typeof v === 'boolean') {
    return booleanCol;
  }
  if (v instanceof Date) {
    return timestampCol;
  }
  // `Dataset` ⇒ a nested / TS-ref column: recover `nested-dataset`
  // from the cell's own schema so post-join per-cell `unnest` /
  // `time-bucket` dispatch survives. `inferColumnTypeFromCells`
  // unifies via `columnTypesEqual` (structural `schemasEqual` on the
  // inner schema), so rows whose nested schemas disagree, or a
  // dataset mixed with a scalar, still safely fall back to `unknown`.
  if (v instanceof Dataset) {
    return nestedDatasetCol(v.schema);
  }
  // Arrays ⇒ not synthesized as `array` (element type is not cheaply
  // unifiable here); stay `unknown`.
  if (Array.isArray(v)) {
    return unknownCol;
  }
  if (v !== null && typeof v === 'object') {
    return structCol(
      Object.fromEntries(
        Object.entries(v as Record<string, unknown>).map(([k, fv]) => [k, fv == null ? unknownCol : inferOne(fv)]),
      ),
    );
  }
  return unknownCol;
};

/**
 * Unify two per-cell inferred `ColumnType`s for the same column, FIELD-WISE
 * for structs so a member that is null/absent in some rows does not collapse
 * the whole column. `unknownCol` is absorbed by a concrete type ONLY inside
 * struct-field unification — never at the top level, where a genuine scalar
 * conflict (e.g. number vs string) still degrades to `unknownCol`.
 *
 * Unit hints unify field-wise too: `columnTypesEqual` ignores the unit
 * metadata, so the equal-types fast path re-derives the hint via
 * `unifiedUnitOfTypes` — agreement keeps it; differing units, or a unitless
 * number alongside a unit-bearing one, strip the unit but keep the kind
 * ("column unit only when EVERY non-null cell agrees").
 */
const unifyInferredTypes = (a: ColumnType, b: ColumnType): ColumnType => {
  if (columnTypesEqual(a, b)) {
    return replaceUnit(a, unifiedUnitOfTypes([a, b]));
  }
  if (a.kind === 'nullable' || b.kind === 'nullable') {
    const innerUnified = unifyInferredTypes(unwrapNullable(a), unwrapNullable(b));
    return innerUnified.kind === 'unknown' ? unknownCol : nullableCol(innerUnified);
  }
  const ua = unwrapNullable(a);
  const ub = unwrapNullable(b);
  if (ua.kind === 'struct' && ub.kind === 'struct') {
    const fieldNames = [...new Set([...Object.keys(ua.fields), ...Object.keys(ub.fields)])];
    const mergedFields = Object.fromEntries(
      fieldNames.map((name) => {
        const aField = ua.fields[name];
        const bField = ub.fields[name];
        if (aField !== undefined && bField !== undefined) {
          if (aField.kind === 'unknown') {
            return [name, bField.kind === 'unknown' ? unknownCol : nullableCol(bField)];
          }
          if (bField.kind === 'unknown') {
            return [name, nullableCol(aField)];
          }
          return [name, unifyInferredTypes(aField, bField)];
        }
        const present = aField ?? (bField as ColumnType);
        return [name, present.kind === 'unknown' ? unknownCol : nullableCol(present)];
      }),
    );
    return structCol(mergedFields);
  }
  return unknownCol;
};

/**
 * Infer one column's `ColumnType` from its cells. Skips null/undefined;
 * the first non-null cell sets the candidate and every later non-null
 * cell is unified with it — structs merge field-wise (a member null/absent
 * in some rows becomes `nullable`), scalar conflicts and any unclassifiable
 * cell ⇒ `unknownCol`. All-null ⇒ `unknownCol` (never guess). A column of
 * unit-boxed numbers earns its unit only when EVERY non-null cell carries
 * the same one — any mix (differing units, or boxed alongside plain) keeps
 * the `number` kind but drops the unit.
 */
export const inferColumnTypeFromCells = (cells: readonly Cell[]): ColumnType => {
  let candidate: ColumnType | undefined;
  for (const cell of cells) {
    if (cell == null) {
      continue;
    }
    const next = inferOne(cell);
    if (next.kind === 'unknown') {
      return unknownCol;
    }
    candidate = candidate === undefined ? next : unifyInferredTypes(candidate, next);
    if (candidate.kind === 'unknown') {
      return unknownCol;
    }
  }
  return candidate ?? unknownCol;
};

/**
 * Post-materialisation schema refinement — the single seam where a schema
 * is reconciled against the cells it describes.
 *
 * STRUCTURAL typing is fill-only: a column's type is replaced by the
 * cell-inferred one only when the declared type is `unknown` (through any
 * `nullable` wrapper). A concrete declared kind is authoritative, an
 * `unknown` that can't be inferred stays `unknown`, and the static
 * type-check (which runs on the still-`unknown` plan schema) is
 * unaffected.
 *
 * The UNIT of a numeric column is value-level truth (boxed cells in,
 * column unit derived out), so it is a projection of the cells rather
 * than a fill: every numeric cell boxed with one shared unit ⇒ the column
 * carries that unit; any unitless numeric cell or unit conflict ⇒ none —
 * stripping a stale declared hint. A column with no numeric cells at all
 * (empty / all-null) offers no evidence and keeps its declared type and
 * unit unchanged. `replaceUnit` applies the projection without touching
 * kind, nullability, or stored-unit metadata.
 */
export const refineSchema = (schema: Schema, columns: Columns): Schema =>
  schemaFromEntries(
    Array.from(schema.entries(), ([name, declared]): readonly [string, ColumnType] => {
      const cells = columns[name] ?? [];
      if (unwrapNullable(declared).kind === 'unknown') {
        const inferred = inferColumnTypeFromCells(cells);
        return [name, inferred.kind === 'unknown' ? declared : inferred];
      }
      if (isNumericColumn(declared) && cells.some((c) => typeof c === 'number' || isUnitCell(c))) {
        return [name, replaceUnit(declared, unifiedUnitOfCells(cells))];
      }
      return [name, declared];
    }),
  );
